import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Button } from 'react-bootstrap';
import { GameHeaderBar } from './GameHeaderBar';

// Color mixing pairs
const colorPairs = [
  { color1: 'red', color2: 'blue', result: 'purple', emoji: '💜' },
  { color1: 'red', color2: 'yellow', result: 'orange', emoji: '🧡' },
  { color1: 'blue', color2: 'yellow', result: 'green', emoji: '💚' },
  { color1: 'red', color2: 'white', result: 'pink', emoji: '💖' },
  { color1: 'black', color2: 'white', result: 'gray', emoji: '🩶' }
];

export const ColorMixingGame = ({ handleClose, onGameComplete }) => {
  const [currentPair, setCurrentPair] = useState(0);
  const [selectedColors, setSelectedColors] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [showWrong, setShowWrong] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [mixing, setMixing] = useState(false);

  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
  }, []);

  // Speak instruction when new color pair appears
  useEffect(() => {
    const pair = colorPairs[currentPair];
    if (pair && !isMuted && !showResult && !showWrong) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(
          `Mix ${pair.color1} and ${pair.color2} to make ${pair.result}`
        );
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 600);

      return () => clearTimeout(speakTimeout);
    }
  }, [currentPair, isMuted, volume, showResult, showWrong]);

  const handleColorSelect = (color) => {
    // Locked while two colors are being mixed / the result is showing -
    // changing the picks then queued a second checkAnswer (a "wrong" over a
    // right answer, or the last pair completing - and saving - twice).
    if (mixing || showResult || showWrong) return;

    // Allow deselection if already selected
    if (selectedColors.includes(color)) {
      setSelectedColors(selectedColors.filter(c => c !== color));
      return;
    }
    
    if (selectedColors.length < 2) {
      const newColors = [...selectedColors, color];
      setSelectedColors(newColors);

      if (newColors.length === 2) {
        setMixing(true);
        setTimeout(() => {
          checkAnswer(newColors);
        }, 1500);
      }
    }
  };

  const checkAnswer = (colors) => {
    const pair = colorPairs[currentPair];
    const isCorrect = 
      (colors[0] === pair.color1 && colors[1] === pair.color2) ||
      (colors[0] === pair.color2 && colors[1] === pair.color1);

    if (isCorrect) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }
      setShowResult(true);
      const newCorrectCount = correctCount + 1;
      setCorrectCount(newCorrectCount);
      
      setTimeout(() => {
        if (currentPair < colorPairs.length - 1) {
          setCurrentPair(prev => prev + 1);
          resetRound();
        } else {
          const stars = 3;
          const score = newCorrectCount * 20;
          confetti({ particleCount: 200, spread: 90 });
          setTimeout(() => {
            onGameComplete?.(stars, score);
            handleClose?.();
          }, 1500);
        }
      }, 2000);
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }
      setShowWrong(true);
      setTimeout(() => {
        setShowWrong(false);
        resetRound();
      }, 1500);
    }
  };

  const resetRound = () => {
    setSelectedColors([]);
    setShowResult(false);
    setMixing(false);
  };

  const getColorStyle = (color) => {
    const colors = {
      red: '#ff3b3b',
      blue: '#4285f4',
      yellow: '#ffd700',
      white: '#ffffff',
      black: '#333333',
      purple: '#9c27b0',
      orange: '#ff9800',
      green: '#4caf50',
      pink: '#ff69b4',
      gray: '#9e9e9e'
    };
    return colors[color] || color;
  };

  const pair = colorPairs[currentPair];

  return (
    <>
      <div className="tap-letter-board-bg">
      <GameHeaderBar
        scoreIcon="🎨"
        score={correctCount}
        total={colorPairs.length}
        questionLabel="Mix:"
        question={pair ? `${pair.color1} + ${pair.color2}` : ''}
        volume={volume}
        onVolumeChange={(v) => {
          setVolume(v);
          localStorage.setItem('tapGameVolume', v);
        }}
        onReplay={() => {
          if (pair && !isMuted) {
            const utterance = new window.SpeechSynthesisUtterance(`Mix ${pair.color1} and ${pair.color2} to make ${pair.result}`);
            utterance.rate = 0.8;
            utterance.volume = volume;
            window.speechSynthesis.speak(utterance);
          }
        }}
      />

      <div className="tap-letter-btn-grid mb-4">
        <div 
          className={`mixing-bowl ${showWrong ? 'shake' : ''}`}
          style={{
            background: selectedColors.length === 0 ? '#f5f5f5' :
                       selectedColors.length === 1 ? getColorStyle(selectedColors[0]) :
                       showResult ? getColorStyle(pair.result) :
                       `linear-gradient(45deg, ${getColorStyle(selectedColors[0])} 50%, ${getColorStyle(selectedColors[1])} 50%)`
          }}
        >
          {showResult && (
            <div className="result-emoji">{pair.emoji}</div>
          )}
          {showWrong && (
            <div className="text-white" style={{ fontSize: '2.5rem' }}>❌</div>
          )}
          {!showResult && !showWrong && selectedColors.length === 2 && (
            <div className="text-white" style={{ fontSize: '2rem' }}>🌀</div>
          )}
          {selectedColors.length === 1 && !showResult && !showWrong && (
            <div style={{ fontSize: '1.2rem', color: '#666', fontWeight: 'bold' }}>
              Pick one more!
            </div>
          )}
        </div>
      </div>

        {!showResult && !showWrong && (
        <>
          <p className="text-white text-center mb-3" style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            {selectedColors.length === 0 ? 'Select two colors to mix!' : 
             selectedColors.length === 1 ? 'Select the second color!' : 'Mixing...'}
          </p>
          
          <div className="color-palette">
            {['red', 'blue', 'yellow', 'white', 'black'].map(color => (
              <div
                key={color}
                className={`color-circle ${selectedColors.includes(color) ? 'selected' : ''}`}
                style={{ 
                  background: getColorStyle(color),
                  cursor: selectedColors.length < 2 || selectedColors.includes(color) ? 'pointer' : 'not-allowed',
                  opacity: selectedColors.length === 2 && !selectedColors.includes(color) ? 0.3 : 1
                }}
                onClick={() => handleColorSelect(color)}
                title={selectedColors.includes(color) ? 'Click to deselect' : 'Click to select'}
              >
                {selectedColors.includes(color) && (
                  <div style={{ 
                    color: '#fff', 
                    fontSize: '2rem', 
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                    fontWeight: 'bold'
                  }}>
                    ✓
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {showResult && (
        <div className="text-center">
          <h2 className="text-white mb-3" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            🎉 You made {pair.result.toUpperCase()}! 🎉
          </h2>
        </div>
      )}

      {showWrong && (
        <div className="text-center">
          <h2 className="text-white mb-3" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            ❌ Try Again! That doesn't make {pair.result}!
          </h2>
        </div>
      )}

      <style>{`
        .tap-letter-board-bg {
          background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
          background-size: cover, cover;
          background-position: center, center;
          border-radius: 32px;
          box-shadow: 0 6px 32px #e0c68a33;
          padding: 32px 12px 24px 12px;
          margin: 0 auto;
          max-width: 900px;
          width: 98%;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes board-pop {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .tap-header-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(224,198,138,0.10);
          padding: 1px 18px 18px 18px;
          margin-bottom: 18px;
          gap: 10px;
        }
        .tap-score-box,
        .tap-volume-box {
          display: flex;
          align-items: center;
          border-radius: 14px;
          padding: 6px 14px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #b8860b;
          background: rgba(255, 255, 255, 0.86);
          min-width: 110px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
        }
        .tap-score-label {
          margin-right: 6px;
          font-size: 1rem;
          color: #b8860b;
        }
        .tap-score-value {
          font-size: 1.1rem;
          color: #1976d2;
          margin-left: 4px;
          font-weight: 700;
        }
        .tap-letter-target {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(224,198,138,0.92);
          border-radius: 18px;
          padding: 8px 32px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          margin: 0 18px;
          min-width: 180px;
          flex: 1;
        }
        .tap-letter-label {
          font-size: 1.1rem;
          color: #3e2723;
          margin-right: 8px;
          font-weight: 600;
        }
        .tap-letter-value {
          font-size: 1.5rem;
          color: #1976d2;
          font-weight: 900;
          text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
        }
        .tap-volume-value {
          margin-left: 8px;
          font-size: 1rem;
          color: #1976d2;
          font-weight: 700;
          min-width: 36px;
          display: inline-block;
          text-align: right;
        }
        .mixing-bowl {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 1.5rem auto;
          border: 5px solid #fff;
          box-shadow: 0 8px 25px rgba(0,0,0,0.2);
          transition: all 0.5s ease;
          position: relative;
        }
        .color-circle {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          border: 4px solid #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .color-circle:hover {
          transform: scale(1.1);
          box-shadow: 0 8px 25px rgba(0,0,0,0.3);
        }
        .color-circle.selected {
          transform: scale(0.95);
          border: 6px solid #ffd700;
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
        }
        .result-emoji {
          font-size: 4rem;
          animation: bounceIn 0.5s ease;
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        .shake {
          animation: shake 0.5s ease;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
          20%, 40%, 60%, 80% { transform: translateX(10px); }
        }
        .mixing-animation {
          animation: mix 1.5s ease-in-out;
        }
        @keyframes mix {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        .color-palette {
          display: flex;
          gap: 0.8rem;
          justify-content: center;
          flex-wrap: wrap;
          margin: 1rem 0;
        }
        .result-emoji {
          font-size: 4rem;
          animation: bounceIn 0.5s ease;
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        @media (max-width: 700px) {
          .tap-header-bar {
            flex-direction: column;
            gap: 10px;
            padding-bottom: 10px;
          }
          .tap-letter-target {
            min-width: 0;
            width: 100%;
            padding: 8px 0;
            margin: 8px 0;
          }
          .mixing-bowl {
            width: 140px;
            height: 140px;
            margin: 1rem auto;
          }
          .color-circle {
            width: 72px;
            height: 72px;
          }
          .result-emoji {
            font-size: 3rem;
          }
        }
        @media (max-width: 480px) {
          .tap-letter-board-bg {
            padding: 10px;
          }
          .mixing-bowl {
            width: 120px;
            height: 120px;
          }
          .color-circle {
            width: 60px;
            height: 60px;
          }
          .color-palette {
            gap: 0.5rem;
          }
        }
      `}</style>
      </div>
    </>
  );
};